import { useCallback } from 'react';
import useAsync from 'src/hooks/useAsync';
import axiosInstance from 'src/utils/axios';

interface BatchResponseDto {
  success: boolean;
  message?: string;
  errors?: string[];
}

interface SetToReadParams {
  clientId: string;
  messageId: string;
}

const useSetMessageToReadApi = () => {
  const setToReadCall = useCallback(async (params?: SetToReadParams) => {
    if (!params) {
      throw new Error('Parameters are required');
    }
    
    const { clientId, messageId } = params;
    
    try {
      if (!clientId || !messageId) {
        throw new Error('Client ID and Message ID are required');
      }
      
      const response = await axiosInstance.post<BatchResponseDto>(
        `Message/SetToRead/${clientId}/${messageId}`,
        {}, // Empty body for POST request
        {
          // Let axios interceptor handle authorization
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('SetToRead API error:', {
        clientId,
        messageId,
        error: error,
        response: error.response,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });

      // Create more specific error message
      let errorMessage = 'Unknown error';
      if (error.response) {
        errorMessage = `HTTP ${error.response.status}: ${error.response.statusText}`;
        if (error.response.data) {
          if (typeof error.response.data === 'string') {
            errorMessage += ` - ${error.response.data}`;
          } else if (error.response.data.message) {
            errorMessage += ` - ${error.response.data.message}`;
          }
        }
      } else if (error.request) {
        errorMessage = 'No response from server';
      } else if (error.message) {
        errorMessage = error.message;
      }

      throw new Error(`Failed to set message to read: ${errorMessage}`);
    }
  }, []);

  const { execute, isLoading, error, value } = useAsync(setToReadCall, false);

  return {
    setMessageToRead: execute,
    isSettingToRead: isLoading,
    setToReadError: error,
    setToReadResult: value,
  };
};

export default useSetMessageToReadApi;